/*
 * Mapping (+ page group) operations for the Demo Environment Management page.
 * Datasource-level entity. Mirrors sales-demo-env-setup's
 * `src/lib/mapping-page-group.js` (create/list/delete subset used by the CLI
 * wrappers), adapted to use `CsqApiClient`. "Create" is always replicated
 * from Source (see `buildMappingsCreateFromSourceOperations` below), used by
 * the standalone "Create from Source"/"Resync" action and by
 * `createAllEntityTypes` (entity type "all") in csqEntityHandlers.js.
 */

/**
 * Creates a mapping + page group in one call.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ pageGroupData: object, mappingData: object }} data
 */
export async function createMappingAndPageGroup(client, datasourceId, { pageGroupData, mappingData }) {
  return client.post(`/v2/projects/${datasourceId}/page-group/create`, { pageGroupData, mappingData });
}

/**
 * Adds an additional page group to an ALREADY-EXISTING mapping (as opposed to
 * `createMappingAndPageGroup`, which always creates a brand-new mapping).
 * Used to recreate a source mapping's full set of pages under a single new
 * target mapping (see "Create from Source" in entities-tab.js): the first
 * page uses `createMappingAndPageGroup`, every subsequent page uses this.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {string|number} mappingId
 * @param {object} pageGroupData
 */
export async function addPageGroupToMapping(client, datasourceId, mappingId, pageGroupData) {
  return client.post(`/v2/projects/${datasourceId}/mappings/${mappingId}/page-group/create`, { pageGroupData });
}

/**
 * Lists all mappings in a datasource.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ take?: number, fields?: string[] }} [options]
 * @returns {Promise<Array<{ id: number, name: string }>>}
 */
export async function listMappings(client, datasourceId, { take = 1000, fields = ['id', 'name'] } = {}) {
  const query = new URLSearchParams();
  fields.forEach((field) => query.append('fields[]', field));
  query.append('skip', '0');
  query.append('take', String(take));

  const payload = await client.get(`/v1/projects/${datasourceId}/mappings?${query.toString()}`);
  const mappings = Array.isArray(payload) ? payload : payload.items || [];
  return mappings.map((mapping) => ({ id: mapping.id, name: mapping.name }));
}

/**
 * Deletes a single mapping (and its linked pages/items).
 */
export async function deleteMapping(client, datasourceId, mappingId) {
  return client.del(`/v1/projects/${datasourceId}/mappings/${mappingId}/with-linkeditems`);
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const DEFAULT_MAPPING_WITH_PAGES_FIELDS = ['id', 'name', 'description', 'isReferenceMapping'];
const DEFAULT_PAGE_WITH_PAGES_FIELDS = [
  'id',
  'projectId',
  'name',
  'target',
  'categoryId',
  'color',
  'previewUrl',
  'type',
  'linkedObjectCount',
];

/**
 * Lists every mapping in a datasource together with its pages (page groups),
 * nested under `pages`. Used to recreate mappings+pages on another datasource
 * (see "Create from Source" in entities-tab.js).
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ mappingFields?: string[], pageFields?: string[] }} [options]
 * @returns {Promise<Array<{ id: number, name: string, description: string|null, isReferenceMapping: boolean, pages: Array<object> }>>}
 */
export async function listMappingsWithPages(
  client,
  datasourceId,
  { mappingFields = DEFAULT_MAPPING_WITH_PAGES_FIELDS, pageFields = DEFAULT_PAGE_WITH_PAGES_FIELDS } = {},
) {
  const take = 500;
  let skip = 0;
  const mappings = [];
  // `paged-with-pages` is paginated route — loop using `total` so a
  // datasource with more than one page of mappings isn't silently truncated.
  for (;;) {
    const payload = await client.post(`/api/mappings-composite/v1/projects/${datasourceId}/mappings/paged-with-pages`, {
      mappingFields,
      pageFields,
      includeSourceMapping: false,
      dimensions: {},
      pagination: { skip, take },
      search: { text: '', scope: 'pages' },
    });
    const items = (payload && payload.items) || [];
    mappings.push(...items);
    skip += take;
    if (items.length < take || skip >= (payload.total || 0)) break;
    await sleep(200);
  }
  return mappings;
}

/**
 * Builds the "create from source" operations plan for mappings: one new target mapping per
 * source mapping, carrying all of its pages/page-groups. Shared by the standalone "Create from
 * Source" action, by "Resync", and by `createAllEntityTypes` (entity type "all").
 *
 * @param {object} options
 * @param {import('./csqApiClient').CsqApiClient} options.sourceClient
 * @param {import('./csqApiClient').CsqApiClient} options.targetClient
 * @param {string|number} options.sourceDatasourceId
 * @param {Array<string|number>} options.targetDatasourceIds
 * @param {(message: string) => void} options.log
 */
export async function buildMappingsCreateFromSourceOperations({ sourceClient, targetClient, sourceDatasourceId, targetDatasourceIds, log }) {
  log(`Listing mappings+pages for source datasource ${sourceDatasourceId}...`);
  const sourceMappings = await listMappingsWithPages(sourceClient, sourceDatasourceId);
  log(`  found ${sourceMappings.length} source mapping(s).`);

  const operations = [];
  const plannedNames = new Set();
  const collisions = [];

  for (const targetDatasourceId of targetDatasourceIds) {
    log(`Listing existing mappings on target datasource ${targetDatasourceId}...`);
    const existingTargetMappings = await listMappings(targetClient, targetDatasourceId);
    const existingNames = new Set(existingTargetMappings.map((m) => m.name));

    for (const sourceMapping of sourceMappings) {
      const pages = sourceMapping.pages || [];
      if (pages.length === 0) {
        log(`  WARNING: source mapping "${sourceMapping.name}" (${sourceMapping.id}) has no pages, skipping.`);
        continue;
      }
      const plannedName = sourceMapping.name;
      if (existingNames.has(plannedName) || plannedNames.has(`${targetDatasourceId}:${plannedName}`)) {
        collisions.push(`${targetDatasourceId}: "${plannedName}"`);
        continue;
      }
      plannedNames.add(`${targetDatasourceId}:${plannedName}`);
      operations.push({
        targetDatasourceId,
        mappingData: {
          name: plannedName,
          isReferenceMapping: sourceMapping.isReferenceMapping || false,
          isReferenceMappingTaxonomy: false,
        },
        pageGroups: pages.map((page) => ({
          name: page.name,
          categoryId: page.categoryId,
          color: page.color,
          target: page.target,
          deletePageGroupIfEmpty: true,
          ...(page.previewUrl ? { previewUrl: page.previewUrl } : {}),
        })),
      });
    }
  }

  return { operations, collisions };
}

/**
 * Executes the operations plan built by `buildMappingsCreateFromSourceOperations`: creates one
 * new mapping per source mapping (first page via `createMappingAndPageGroup`, remaining pages
 * attached via `addPageGroupToMapping`).
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {Array<object>} operations
 * @param {(message: string) => void} log
 */
export async function runMappingsCreateFromSourceOperations(client, operations, log) {
  let successCount = 0;
  const failures = [];
  for (const op of operations) {
    const [firstPageGroup, ...restPageGroups] = op.pageGroups;
    try {
      await createMappingAndPageGroup(client, op.targetDatasourceId, {
        pageGroupData: firstPageGroup,
        mappingData: op.mappingData,
      });
      // The create response's exact shape isn't guaranteed, so resolve the new mapping's id
      // by name (already validated collision-free) to reliably attach the remaining pages.
      const targetMappings = await listMappings(client, op.targetDatasourceId);
      const newMapping = targetMappings.find((m) => m.name === op.mappingData.name);
      if (!newMapping) {
        throw new Error(`Mapping "${op.mappingData.name}" was created but could not be found afterwards.`);
      }
      for (const pageGroup of restPageGroups) {
        await addPageGroupToMapping(client, op.targetDatasourceId, newMapping.id, pageGroup);
      }
      successCount += 1;
      log(`  ${op.targetDatasourceId}: created "${op.mappingData.name}" (mapping ${newMapping.id}) with ${op.pageGroups.length} page(s).`);
    } catch (err) {
      failures.push({ ...op, error: err.message });
      log(`  ${op.targetDatasourceId}: "${op.mappingData.name}" FAILED — ${err.message}`);
    }
  }
  return { successCount, failures };
}

const DEFAULT_MAPPING_FIELDS = [
  'id',
  'projectId',
  'name',
  'description',
  'protected',
  'createdAt',
  'updatedAt',
  'isReferenceMapping',
  'isAutoMapping',
];

const DEFAULT_PAGE_FIELDS = ['id', 'name', 'color', 'category', 'categoryId', 'processedAt', 'target'];

/**
 * Lists the pages (page groups) that exist in a datasource, flattened across
 * mappings. Used to resolve a page id by name or url for zoning creation.
 *
 * @param {import('./csqApiClient').CsqApiClient} client
 * @param {string|number} datasourceId
 * @param {{ search?: string, take?: number, skip?: number }} [options]
 */
export async function listPages(client, datasourceId, { search = '', take = 100, skip = 0 } = {}) {
  const payload = await client.post(`/api/mappings-composite/v1/projects/${datasourceId}/mappings/paged-with-pages`, {
    mappingFields: DEFAULT_MAPPING_FIELDS,
    pageFields: DEFAULT_PAGE_FIELDS,
    includeSourceMapping: false,
    dimensions: {},
    pagination: { skip, take },
    search: { text: search, scope: 'pages' },
  });

  const items = (payload && payload.items) || [];
  return items.flatMap((mapping) =>
    (mapping.pages || []).map((page) => ({
      id: page.id,
      name: page.name,
      categoryId: page.categoryId,
      color: page.color,
      target: page.target,
      mappingId: mapping.id,
      mappingName: mapping.name,
    })),
  );
}

/**
 * Resolves a single page id for a datasource by name (or the only page, when unambiguous).
 *
 * Several pages can share a name (the same page name under different mappings), and only the
 * source's own page has the screenshots a zoning needs. So when replicating from a source page,
 * pass `mappingName` (the mapping that page lives under) and/or `target` (its filter tree) to
 * narrow same-named candidates down to the page that mirrors the source one.
 */
export async function resolvePage(client, datasourceId, { pageName, mappingName, target, log } = {}) {
  const pages = await listPages(client, datasourceId, { search: pageName || '' });

  if (pages.length === 0) {
    throw new Error(`No pages found for datasource ${datasourceId}. Create a page group first.`);
  }

  if (pageName) {
    const wanted = pageName.trim().toLowerCase();
    const matches = pages.filter((page) => page.name.trim().toLowerCase() === wanted);
    if (matches.length === 0) {
      const available = pages.map((page) => `"${page.name}" (id ${page.id})`).join(', ');
      throw new Error(`No page named "${pageName}" in datasource ${datasourceId}. Available: ${available}.`);
    }
    let candidates = matches;
    if (candidates.length > 1 && mappingName) {
      const wantedMapping = mappingName.trim().toLowerCase();
      const inMapping = candidates.filter((page) => (page.mappingName || '').trim().toLowerCase() === wantedMapping);
      if (inMapping.length > 0) {
        candidates = inMapping;
      } else if (log) {
        log(`  WARNING: no page "${pageName}" under mapping "${mappingName}" in datasource ${datasourceId} — falling back to matching by page name only.`);
      }
    }
    if (candidates.length > 1 && target) {
      const sameTarget = candidates.filter((page) => page.target === target);
      if (sameTarget.length > 0) candidates = sameTarget;
    }
    if (candidates.length === 1) {
      return { id: candidates[0].id, name: candidates[0].name };
    }
    if (candidates.length > 1) {
      // Ambiguous by name (e.g. the same page path mapped under more than one page group) — rather
      // than hard-failing the whole create, default to the first match (in listing order) and log
      // the decision, so a Resync/Create-all run isn't blocked by a naming collision the source
      // datasource already tolerates.
      const [chosen, ...rest] = candidates;
      const otherIds = rest.map((page) => page.id).join(', ');
      if (log) log(`  WARNING: multiple pages named "${pageName}" in datasource ${datasourceId} (ids: ${chosen.id}, ${otherIds}) — defaulting to the first one (id ${chosen.id}).`);
      return { id: chosen.id, name: chosen.name };
    }
    return { id: matches[0].id, name: matches[0].name };
  }

  if (pages.length > 1) {
    const available = pages.map((page) => `"${page.name}" (id ${page.id})`).join(', ');
    throw new Error(`Datasource ${datasourceId} has ${pages.length} pages. Pass a page id or name. Available: ${available}.`);
  }

  return { id: pages[0].id, name: pages[0].name };
}

/**
 * Evaluates a single `page:path` filter against a URL path.
 */
function pathMatchesFilter(operator, path, value) {
  const p = String(path).toLowerCase();
  const v = String(value).toLowerCase();
  const pTrim = p.replace(/^\/+/, '').replace(/\/+$/, '');
  const vTrim = v.replace(/^\/+/, '').replace(/\/+$/, '');
  switch (operator) {
    case 'equals':
      return p === v || pTrim === vTrim;
    case 'not_equals':
      return !(p === v || pTrim === vTrim);
    case 'contains':
      return p.includes(v) || pTrim.includes(vTrim);
    case 'not_contains':
      return !(p.includes(v) || pTrim.includes(vTrim));
    case 'starts_with':
      return pTrim.startsWith(vTrim);
    case 'ends_with':
      return pTrim.endsWith(vTrim);
    default:
      return p.includes(v);
  }
}

/**
 * Walks a page target tree (OR -> AND -> filter) and tells whether the given
 * URL path satisfies it. Only `page:path` filters are evaluated; other fields
 * are treated as satisfied so they do not block a match.
 */
function targetMatchesPath(node, path) {
  if (!node || typeof node !== 'object') return false;
  if (node.nodeType === 'OR') {
    return (node.children || []).some((child) => targetMatchesPath(child, path));
  }
  if (node.nodeType === 'AND') {
    return (node.children || []).every((child) => targetMatchesPath(child, path));
  }
  if (node.nodeType === 'filter') {
    if (node.field !== 'page:path') return true;
    return pathMatchesFilter(node.operator, path, node.value);
  }
  return false;
}

/**
 * Resolves a single page for a datasource from a representative URL by matching
 * the URL path against each existing page's target (page:path filters).
 */
export async function resolvePageByUrl(client, datasourceId, url) {
  const { pathname } = new URL(url);
  const pages = await listPages(client, datasourceId, { take: 500 });

  if (pages.length === 0) {
    throw new Error(`No pages found for datasource ${datasourceId}. Create a page group first.`);
  }

  const matches = pages.filter((page) => {
    if (!page.target) return false;
    let tree;
    try {
      tree = JSON.parse(page.target);
    } catch (e) {
      return false;
    }
    return targetMatchesPath(tree, pathname);
  });

  if (matches.length === 0) {
    const available = pages.map((page) => `"${page.name}" (id ${page.id})`).join(', ');
    throw new Error(`No page matches url path "${pathname}" in datasource ${datasourceId}. Available: ${available}.`);
  }

  return { id: matches[0].id, name: matches[0].name, target: matches[0].target };
}
